/**
 * ============================================================
 * Yeni kabuk menüsü (Faz 0) — tek kaynak
 * ============================================================
 * 10 ana öğe + Labs. Her öğe, henüz yeniden yazılmamış eski sayfaları
 * sekme olarak toplar; eski adresler olduğu gibi çalışır. Bir sayfa
 * yeniden yazıldığında yalnız burada sekmesi güncellenir.
 *
 * Kural: bir yol (path) yalnız BİR bölümde yer alır — aktif bölüm
 * yoldan bulunur.
 */
import type { ComponentType } from "react";
import {
  SunOutlined,
  TeamOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  ReadOutlined,
  StarOutlined,
  AccountBookOutlined,
  CheckSquareOutlined,
  FolderOpenOutlined,
  SettingOutlined,
  SoundOutlined,
  ThunderboltOutlined,
  PlayCircleOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";

export interface NavTab {
  label: string;
  path: string;
}

export type NavGroup = "main" | "team" | "labs";

export interface NavSection {
  key: string;
  label: string;
  icon: ComponentType;
  group: NavGroup;
  /** Menü tıklanınca gidilen yol = ilk sekme */
  tabs: NavTab[];
  /** ⌘K'da bölüm için ek arama kelimeleri */
  keywords?: string[];
  /** Sekmede görünmeyen ama bu bölüme ait yol önekleri (eski ayrıntılı ekranlar) */
  extraPaths?: string[];
}

export const NAV: NavSection[] = [
  {
    key: "bugun",
    label: "Bugün",
    icon: SunOutlined,
    group: "main",
    tabs: [
      { label: "Bugün", path: "/" },
      { label: "Göstergeler", path: "/gosterge" },
    ],
    keywords: ["ana sayfa", "gösterge", "dashboard", "özet", "grafik", "iş listesi"],
  },
  {
    key: "kisiler",
    label: "Kişiler",
    icon: TeamOutlined,
    group: "main",
    tabs: [
      { label: "Kişiler", path: "/kisiler" },
      { label: "Aday listeleri", path: "/leads" },
      { label: "Öğrenciler", path: "/ogrenciler" },
      { label: "Ders hakları", path: "/credits" },
      { label: "Seviye testleri", path: "/placements" },
      { label: "Demolar", path: "/demo" },
    ],
    keywords: ["aday", "lead", "öğrenci", "müşteri", "kredi", "seviye", "demo"],
  },
  {
    key: "takvim",
    label: "Takvim",
    icon: CalendarOutlined,
    group: "main",
    tabs: [
      { label: "Takvim", path: "/takvim" },
      { label: "Randevu listesi", path: "/appointments" },
    ],
    keywords: ["randevu", "ders", "özel ders", "deneme", "seans", "program", "ajanda", "ekip takvimi", "ekip etkinliği", "toplantı"],
  },
  {
    key: "programlar",
    label: "Programlar",
    icon: AppstoreOutlined,
    group: "main",
    tabs: [
      { label: "Genel bakış", path: "/programlar" },
      { label: "Grup dersleri", path: "/groups" },
      { label: "Speaking Club", path: "/speaking-club" },
      { label: "Eğitim koçluğu", path: "/coaching" },
    ],
    keywords: ["grup", "cohort", "kulüp", "koçluk", "sınıf"],
  },
  {
    key: "icerik",
    label: "Ödev & Materyal",
    icon: ReadOutlined,
    group: "main",
    tabs: [
      { label: "Genel bakış", path: "/odev" },
      { label: "Ödevler", path: "/odev/liste" },
      { label: "Ders planları", path: "/odev/planlar" },
      { label: "Kütüphane", path: "/kutuphane" },
      { label: "Geri bildirim", path: "/geri-bildirim" },
    ],
    keywords: ["kitap", "ödev", "materyal", "kütüphane", "dosya", "pdf", "yorum", "anket", "notlama", "hatırlatma", "teslim", "plan", "müfredat", "ünite", "memnuniyet", "nps", "değerlendirme"],
  },
  {
    key: "egitmenler",
    label: "Eğitmenler",
    icon: StarOutlined,
    group: "main",
    tabs: [
      { label: "Genel bakış", path: "/egitmenler/ozet" },
      { label: "Eğitmen listesi", path: "/egitmenler" },
      { label: "Başvurular", path: "/teacher-applications" },
      { label: "Müsaitlik", path: "/availability" },
    ],
    keywords: ["öğretmen", "hoca", "başvuru", "müsaitlik"],
  },
  {
    key: "finans",
    label: "Finans",
    icon: AccountBookOutlined,
    group: "main",
    tabs: [
      { label: "Genel bakış", path: "/finans" },
      { label: "Tahsilatlar", path: "/finans/tahsilat" },
      { label: "Giderler", path: "/finans/gider" },
      { label: "Eğitmen ödemeleri", path: "/finans/egitmen" },
      { label: "Kasa & hesaplar", path: "/finans/kasa" },
      { label: "Hareketler", path: "/maliye/transactions" },
      { label: "Raporlar", path: "/maliye/reports" },
      { label: "Kuponlar", path: "/coupons" },
    ],
    // Sekmede yok ama bu bölüme ait eski ayrıntılı ekranlar (alacak-borç, bütçe, tekrarlayan, hesaplar, eski eğitmen cüzdanı)
    extraPaths: ["/maliye", "/finance"],
    keywords: ["maliye", "para", "ödeme", "tahsilat", "taksit", "borç", "alacak", "fatura", "hakediş", "eğitmen ödemesi", "kasa sayımı", "aktarım", "düzenli gider", "kasa", "gelir", "gider", "kupon", "rapor"],
  },
  {
    key: "gorevler",
    label: "Görevler & Toplantılar",
    icon: CheckSquareOutlined,
    group: "team",
    tabs: [
      { label: "Görevler", path: "/gorevler" },
      { label: "Toplantılar", path: "/gorevler/toplantilar" },
      { label: "Pano", path: "/gorevler/pano" },
    ],
    // /planlama/* eski adresleri yeni yerlerine yönlenir (ekip takvimi → Takvim "Ekip" katmanı)
    extraPaths: ["/planlama"],
    keywords: ["planlama", "görev", "toplantı", "not", "yapılacak", "sahipsiz", "ertele", "iş", "haftalık", "karar", "rapor"],
  },
  {
    key: "iletisim",
    label: "İletişim",
    icon: SoundOutlined,
    group: "team",
    tabs: [
      { label: "Duyurular", path: "/announcements" },
      { label: "Bildirimler", path: "/notifications" },
      { label: "Mesajlar", path: "/messages" },
    ],
    keywords: ["duyuru", "bildirim", "mesaj", "push", "herkese yaz", "öğrencilere duyur", "eğitmenlere duyur"],
  },
  {
    key: "ayarlar",
    label: "Ayarlar",
    icon: SettingOutlined,
    group: "team",
    tabs: [
      { label: "Genel", path: "/ayarlar" },
      { label: "Ekip ve erişim", path: "/ayarlar/ekip" },
      { label: "Hesaplar", path: "/profiles" },
      { label: "Kayıtlar", path: "/ayarlar/kayitlar" },
    ],
    // Eski adresler yeni sekmelere yönlenir
    extraPaths: ["/settings", "/notification-settings", "/audit", "/deleted-accounts"],
    keywords: ["fiyat", "paket", "ücret", "eğitmen ücreti", "iptal süresi", "kullanıcı", "hesap", "e-posta bildirimi", "alıcı", "işlem geçmişi", "denetim", "silinen hesap", "deneme eğitmeni", "yorum bağlantısı", "pasif"],
  },
  // ── Labs: yalnız geliştirici modu açık hesapta görünür ──
  {
    key: "buyume",
    label: "Büyüme",
    icon: ThunderboltOutlined,
    group: "labs",
    tabs: [
      { label: "Gelen kutusu", path: "/buyume" },
      { label: "Reklam merkezi", path: "/buyume/reklamlar" },
      { label: "AI Closer", path: "/buyume/ai" },
      { label: "İçerik stüdyosu", path: "/buyume/icerik" },
      { label: "Reaktivasyon", path: "/buyume/reaktivasyon" },
      { label: "Kampanyalar", path: "/campaigns" },
      { label: "Kanallar & kurulum", path: "/buyume/kanallar" },
    ],
    keywords: ["whatsapp", "instagram", "reklam", "meta", "kampanya"],
  },
  {
    key: "dosyalar",
    label: "Dosyalar",
    icon: FolderOpenOutlined,
    group: "labs",
    tabs: [
      { label: "Dosyalar", path: "/drive" },
      { label: "Favoriler", path: "/drive/favoriler" },
      { label: "Aktivite", path: "/drive/aktivite" },
    ],
    // F7.4a: kullanılmıyor (0 dosya) → Labs; kullanılmaya başlarsa group "team" yapılır
    keywords: ["drive", "belge", "paylaşım"],
  },
  {
    key: "kurslar",
    label: "Video kurslar",
    icon: PlayCircleOutlined,
    group: "labs",
    tabs: [{ label: "Kurslar", path: "/courses" }],
  },
  {
    key: "vitrin",
    label: "Bileşen vitrini",
    icon: ExperimentOutlined,
    group: "labs",
    tabs: [{ label: "Bileşen vitrini", path: "/labs/ui" }],
  },
];

export const GROUP_LABEL: Record<NavGroup, string | null> = {
  main: null,
  team: "Ekip",
  labs: "Labs · geliştirici",
};

/**
 * Yol → bölüm + sekme. En uzun eşleşen sekme yolu kazanır; "/" yalnız
 * tam eşleşir. Eşleşme yoksa null (ör. bilinmeyen/eski yol).
 */
export function findNav(pathname: string): { section: NavSection; tab: NavTab } | null {
  let best: { section: NavSection; tab: NavTab } | null = null;
  for (const section of NAV) {
    for (const tab of section.tabs) {
      const hit =
        tab.path === "/"
          ? pathname === "/"
          : pathname === tab.path || pathname.startsWith(tab.path + "/");
      if (hit && (!best || tab.path.length > best.tab.path.length)) {
        best = { section, tab };
      }
    }
  }
  if (!best) {
    for (const section of NAV) {
      const extra = section.extraPaths?.find((p) => pathname === p || pathname.startsWith(p + "/"));
      if (extra) return { section, tab: { label: "", path: extra } };
    }
  }
  return best;
}

export const visibleSections = (labs: boolean): NavSection[] =>
  NAV.filter((s) => s.group !== "labs" || labs);
